import { RespectPriceIncreaseStrategy } from "./GlobalConfig";
import { languageStrings, LanguageSettings } from "../../shared";

/** Texts for the client */
export interface AppTexts {
  generic: {
    logo: string;
    error: {
      other: string;
      timeout: string;
      problemLoading: string;
      technicalProblem: string;
      comeBack: string;
      contactSupport: string;
      serverError: string;
      networkError: string;
    };
    form: {
      fieldRequired: string;
      invalidEmail: string;
    };
    exchange: string;
    return: string;
    goBack: string;
    signOut: string;
    free: string;
    next: string;
    returnAlreadySubmitted: string;
    multipleRefundsPossible: string;
    submitAnotherReturn: string;
  };
  footer: {
    default: string;
    privacyPolicy: string;
  };
  shopNotFound: {
    title: string;
    body: string;
  };
  pageNotFound: {
    title: string;
    body: string;
  };
  noApp: {
    title: string;
    notPossible: string;
    useDifferentBrowser: string;
    orVisitWithPhone: string;
  };
  delivery: {
    other: string;
    otherAddress: string;
  };
  orderLookup: {
    title: string;
    orderNumber: string;
    submit: string;
    pageTitle: string;
    postalcode: string;
    any: string;
    emailaddress: string;
    error: {
      notFound: string;
      currencyNotSupported: string;
    };
    infoMessage?: string;
  };
  summary: {
    pageTitle: string;
    generatingLabel: string;
    labelReady: string;
    downloadLabel: string;
    labelNotPaid: string;
    noLabel: string;
    restartPayment: string;
    mailWillBeSentToPaperless: string;
    mailWillBeSentToPaperlessSub: string;
    mailWillBeSentTo: string;
    mailWillBeSentToOnlyInstructions: string;
    estimatedProcessingTime: string;
    howToTitle: string;
    itemsToSendBack: string;
    itemsToReceive: string;
    title: string;
    thanksSubmitting: string;
    submittingPickUp: string;
    thanksSubmittingBudbee: string;
    paymentLinkReady: string;
    redirectToPayment: string;
    commercialInvoiceReady: string;
    downloadCommercialInvoice: string;
    noCommercialInvoice: string;
    viewDropoffPoints: string;
  };
  item: {
    exchangeInfoPriceIncrease: Record<RespectPriceIncreaseStrategy["type"], string> | string;
    exchangeInfoDiscount: string;
    adjustmentIncrease: string;
    adjustmentDecrease: string;
  };
  rma: { warning: Record<string, string>; option: Record<string, string> };
  itemsList: {
    returnAlreadySubmitted: string;
    paymentPending: {
      title: string;
      message: string;
    };
    ineligibleSubtitle: string;
    expectedArrivalDate: string;
    lastUpdate: string;
    noReturnCountry: string;
    noItemsEligible: string;
    returnWindowClosed: string;
    warrantyReturnPossible: string;
    submitWarrantyRequest: string;
    productsWithTechnicalErrors: string;
    title: string;
    returnableUntil: string;
    pageTitle: string;
    cannotContinueWithReturn: string;
    selectedItems: string;
    adjustmentsInCalculation: string;
    continueWithReturn: string;
  };
  tracking: {
    status: {
      submitted: string;
      droppedOff: string;
      inTransit: string;
      delivered: string;
    };
  };
  review: {
    shippingAddressTitle: string;
    itemsToSendBack: string;
    itemsToReceive: string;
    deliveryOptionTitle: string;
    deliveryOption: {
      billingAddress: string;
      pickupPoint: string;
    };
    error: {
      notAccepted: string;
    };
    comment: {
      commentIsEmpty: string;
      commentIsTooShort: string;
      infoExchange: string;
      infoRefund: string;
      addAddress: string;
      addComment: string;
      clearAddress: string;
      clearComment: string;
    };
    refund: {
      additionalPaymentInfo: string;
      title: string;
      valueOfExchangeProducts: string;
      calculationPriceIncreasement: string;
      valueOfReturnedProducts: string;
      appliedDiscountToOrder: string;
      totalAdditionalPayment: string;
      totalRefundAmount: string;
      shippingCostRefundInfo: string;
    };
    budbee: {
      phoneNumberInvalid: string;
      hasBillingPhoneNumber: string;
      hasNoBillingPhoneNumber: string;
      typePhoneNumberHere: string;
    };
    sendcloud: {
      header: string;
      explanation: string;
      streetNamePlaceholder: string;
      streetNumberPlaceholder: string;
      streetNameInvalid: string;
      streetNumberInvalid: string;
    };
    submitReturnAndPay: string;
    submitReturn: string;
    additionalRefundInfo: string;
    noItemsSelected: string;
    title: string;
    pageTitle: string;
    returnShippingCosts: string;
    removedDiscount: string;
    extraDiscount: string;
  };
  shipping: {
    returnMethod: string;
    pleaseSelectMethod: string;
  };
  itemWizard: {
    chooseVariant: string;
    reasonsInfoBottom: string;
    from: string;
    reasonsWhyExchange: string;
    reasonsWhyReturn: string;
    typeCommentHereAndCount: string;
    typeCommentHere: string;
    submitComment: string;
    commentIsEmpty: string;
    commentIsTooShort: string;
    isCurrentVariant: string;
    outOfStock: string;
    otherVariantInfoTop?: string;
    noProductFound: string;
    otherCollectionInfoTop?: string;
    howMakeItRight: string;
    sendSameProduct: string;
    showMoreOptions: string;
  };
  shippingLabel: {
    isPaidLabel: string;
  };
  languageStrings: languageStrings;
}

type PublicConfigColor = {
  hsbValue: {
    alpha: number;
    brightness: number;
    hue: number;
    saturation: number;
  };
  value: string;
};

export type PublicConfigFont = {
  provider: string;
  value: string;
  url?: string | null;
};

export const DEFAULT_OVERLAY_OPACITY = 0.5;
export const DEFAULT_OVERLAY_COLOR: PublicConfigColor = {
  hsbValue: {
    alpha: 1,
    brightness: 0,
    hue: 0,
    saturation: 0,
  },
  value: "#000000",
};
export interface AppTheme {
  background: string | null;
  backgroundMode?: "image" | "color";
  overlayOpacity: number;
  colors: {
    background: PublicConfigColor;
    primary: PublicConfigColor;
    secondary: PublicConfigColor;
    product: PublicConfigColor;
    overlay: PublicConfigColor;
    /** If not set will fallback to `primary` color with 90% brightness*/
    ristBackground?: PublicConfigColor;
  };
  favicon: {
    png: string | null;
    ico: string | null;
  };
  fonts: {
    primary: PublicConfigFont;
    secondary: PublicConfigFont;
  };
  logo: string | null;
  css?: string | null;
}

export type LoginVerificationMethod = "postalcode" | "emailaddress" | "any";

export interface PublicConfig {
  logo?: string;
  appTitle: string;
  domain: string;
  supportEmail: string;
  privacyPolicyUrl?: string;
  privacyPolicyUrlTranslated?: Record<string, string>;
  isPrivacyPolicyRequired?: boolean;
  loginVerification: LoginVerificationMethod;
  theme: AppTheme;
  headInjectedHtml?: string;
  texts?: Partial<AppTexts>;
  /** Per-locale text overrides for when multi-language is enabled */
  textsByLocale?: Record<string, Partial<AppTexts>>;
  language: string;
  languageSettings?: LanguageSettings;
  warningMessages: {
    tag: string;
    message: string;
  }[];
}
